import React, { ChangeEvent, useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import useTranslation from "../../../shared/hooks/useTranslation";
import Loader from "../../../shared/components/Loader";
import { ServiceReportsState, MachinePartGroup } from "../../machinery/models/ServiceReportsModel";
import { getServiceReports, clearServiceReports } from "../../machinery/customerMachineServiceReportsSlice";
import dayjs from "dayjs";
import { standartDateTimeFormat } from "../../../shared/dateFormats";
import { dt } from "../../../shared/themes/redesignTokens";

interface AttachReportDialogProps {
  open: boolean;
  onClose: () => void;
  selectedMachineId?: string;
  machineParts: MachinePartGroup[];
  onChoose: (reportNumber: string) => void;
}

const AttachReportDialog = ({
  open,
  onClose,
  selectedMachineId,
  machineParts,
  onChoose,
}: AttachReportDialogProps) => {
  const dispatch = useAppDispatch();
  const isPhone = useMediaQuery("(max-width:768px)");

  const { translate } = useTranslation(["myMachinery", "common"]);

  const { serviceReports, loading } = useAppSelector<ServiceReportsState>(
    (state) => state.customerServiceReports
  );

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedReportNumber, setSelectedReportNumber] = useState<string>("");

  // Build flat id->name lookup from machineParts groups
  const partNameById = useMemo(() => {
    const map: Record<string, string> = {};
    if (machineParts) {
      for (const group of machineParts) {
        for (const part of group.parts) {
          map[part.id] = part.name;
        }
      }
    }
    return map;
  }, [machineParts]);

  const handleReportSelect = (reportNumber: string) => {
    setSelectedReportNumber(reportNumber);
  };

  const filteredReports = useMemo(() => {
    return serviceReports?.filter((report) => {
      const q = searchQuery.toLowerCase();
      return (
        report.title.toLowerCase().includes(q) ||
        report.description.toLowerCase().includes(q) ||
        (report.affectedPartCategoryId &&
          partNameById[report.affectedPartCategoryId]?.toLowerCase().includes(q)) ||
        report.number.toLowerCase().includes(q)
      );
    });
  }, [serviceReports, searchQuery, partNameById]);

  const reportsCount = filteredReports?.length ?? 0;

  const handleCancel = () => {
    onClose();
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  useEffect(() => {
    if (open) {
      dispatch(getServiceReports({ machineId: selectedMachineId }));
      setSearchQuery("");
      setSelectedReportNumber("");
    }
    return () => {
      dispatch(clearServiceReports());
    };
  }, [open, dispatch, selectedMachineId]);

  // Inject dialog-specific keyframes
  useEffect(() => {
    const styleId = "attach-report-dialog-keyframes";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        @keyframes attachDialogSlideUp {
          0% { opacity: 0; transform: translateY(24px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: dt.radiusLg,
          overflow: "hidden",
          fontFamily: dt.font,
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          "& *:not(.MuiSvgIcon-root)": { fontFamily: dt.font },
          animation: "attachDialogSlideUp 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
          ...(isPhone && {
            margin: "16px",
            width: "calc(100% - 32px)",
            maxWidth: "none",
            maxHeight: "calc(100vh - 32px)",
          }),
        },
      }}
    >
      {/* ── Gradient accent bar ── */}
      <Box
        sx={{
          height: "4px",
          background: `linear-gradient(90deg, ${dt.blue} 0%, #5DADE2 50%, ${dt.blue}00 100%)`,
        }}
      />

      {/* ── Header ── */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          pb: 0.5,
          pt: 2.5,
          px: 3,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            backgroundColor: dt.blueSoft,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <AssignmentOutlinedIcon sx={{ color: dt.blue, fontSize: 22 }} />
        </Box>
        <Box
          sx={{
            fontWeight: 700,
            fontSize: { xs: "18px", sm: "20px" },
            color: dt.textPrimary,
            letterSpacing: "-0.3px",
          }}
        >
          {translate("myMachinery:loadDataFromYourServiceReports")}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 1, pb: 0, overflow: "hidden", display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
        {/* ── Search ── */}
        <TextField
          fullWidth
          size="small"
          placeholder={translate("myMachinery:nameDescriptionOrOther")}
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlinedIcon
                  sx={{ fontSize: "18px", color: dt.textMuted }}
                />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-root": {
              fontFamily: dt.font,
              fontSize: "14px",
              borderRadius: dt.radiusSm,
              backgroundColor: dt.inputBg,
              height: "44px",
              "& fieldset": { borderColor: "transparent" },
              "&:hover fieldset": { borderColor: dt.cardBorder },
              "&.Mui-focused fieldset": {
                borderColor: dt.blue,
                borderWidth: "1.5px",
              },
            },
          }}
        />

        {/* ── Count ── */}
        {!loading && (
          <Typography
            sx={{
              fontFamily: dt.font,
              fontSize: "12px",
              fontWeight: 600,
              color: dt.textSecondary,
              textTransform: "uppercase",
              letterSpacing: "0.8px",
              mb: 1.5,
            }}
          >
            {reportsCount > 0
              ? reportsCount === 1
                ? translate("myMachinery:oneServiceReport", { count: reportsCount })
                : translate("myMachinery:manyServiceReports", { count: reportsCount })
              : translate("myMachinery:noReports")}
          </Typography>
        )}

        {/* ── Loading ── */}
        {loading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              py: 4,
            }}
          >
            <Loader />
          </Box>
        ) : !selectedMachineId ? (
          /* ── No machine selected ── */
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
              py: 4,
            }}
          >
            <DescriptionOutlinedIcon
              sx={{ fontSize: 48, color: dt.textMuted, opacity: 0.5 }}
            />
            <Typography
              sx={{
                fontFamily: dt.font,
                fontSize: "14px",
                color: dt.textMuted,
                textAlign: "center",
              }}
            >
              {translate("myMachinery:noMachineSelected")}
            </Typography>
          </Box>
        ) : reportsCount === 0 ? (
          /* ── No reports found ── */
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
              py: 4,
            }}
          >
            <DescriptionOutlinedIcon
              sx={{ fontSize: 48, color: dt.textMuted, opacity: 0.5 }}
            />
            <Typography
              sx={{
                fontFamily: dt.font,
                fontSize: "14px",
                color: dt.textMuted,
                textAlign: "center",
              }}
            >
              {translate("myMachinery:noReports")}
            </Typography>
          </Box>
        ) : (
          /* ── Report cards ── */
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              maxHeight: isPhone ? "calc(100vh - 320px)" : "calc(80vh - 260px)",
              overflowY: "auto",
              overflowX: "hidden",
              pr: 0.5,
            }}
          >
            {filteredReports?.map((report, index) => {
              const isSelected = selectedReportNumber === report.number;
              const categoryName = report.affectedPartCategoryId
                ? partNameById[report.affectedPartCategoryId]
                : undefined;

              return (
                <Box
                  key={report.number}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleReportSelect(report.number);
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "stretch",
                    borderRadius: dt.radiusSm,
                    border: `1.5px solid ${isSelected ? dt.blue : dt.cardBorder}`,
                    backgroundColor: isSelected
                      ? "rgba(52, 152, 219, 0.04)"
                      : dt.cardBg,
                    cursor: "pointer",
                    overflow: "hidden",
                    transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)",
                    animation: `fadeUp 0.4s cubic-bezier(0.4,0,0.2,1) ${index * 0.05}s both`,
                    "&:hover": {
                      borderColor: isSelected ? dt.blue : "#D1D5DB",
                      boxShadow: isSelected
                        ? `0 0 0 3px ${dt.blueGlow}`
                        : dt.cardShadowHover,
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  {/* Left accent bar */}
                  <Box
                    sx={{
                      width: "4px",
                      backgroundColor: isSelected ? dt.blue : "transparent",
                      flexShrink: 0,
                      transition: "background-color 0.25s ease",
                    }}
                  />

                  {/* Content */}
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: isPhone ? "column" : "row",
                      alignItems: isPhone ? "flex-start" : "center",
                      justifyContent: "space-between",
                      gap: isPhone ? 1 : 3,
                      p: 2,
                      pl: 2,
                      minWidth: 0,
                    }}
                  >
                    {/* Left: Title + Category */}
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: dt.font,
                          fontSize: "14px",
                          fontWeight: 600,
                          color: isSelected ? dt.blue : dt.textPrimary,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {report.title}
                      </Typography>
                      {categoryName && (
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignSelf: "flex-start",
                            fontFamily: dt.font,
                            fontSize: "11px",
                            fontWeight: 600,
                            color: dt.textSecondary,
                            backgroundColor: dt.inputBg,
                            borderRadius: dt.radiusPill,
                            px: 1,
                            py: "2px",
                          }}
                        >
                          {categoryName}
                        </Box>
                      )}
                    </Box>

                    {/* Middle: Number + Date */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.25,
                        flexShrink: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: dt.fontMono,
                          fontSize: "12px",
                          fontWeight: 500,
                          color: dt.textSecondary,
                          letterSpacing: "0.3px",
                        }}
                      >
                        {report.number || "-"}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: dt.font,
                          fontSize: "12px",
                          color: dt.textMuted,
                        }}
                      >
                        {report.openDate
                          ? dayjs(report.openDate).format(standartDateTimeFormat)
                          : "-"}
                      </Typography>
                    </Box>

                    {/* Right: Checkmark */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 28,
                        flexShrink: 0,
                      }}
                    >
                      {isSelected && (
                        <CheckCircleIcon
                          sx={{
                            color: dt.blue,
                            fontSize: 22,
                            animation: "fadeUp 0.2s ease both",
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>

      {/* ── Footer ── */}
      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
          pt: 2,
          gap: 1,
          flexDirection: isPhone ? "column-reverse" : "row",
          "& > :not(:first-of-type)": {
            ml: isPhone ? "0 !important" : undefined,
          },
        }}
      >
        <Button
          onClick={handleCancel}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: "14px",
            fontFamily: dt.font,
            color: dt.textSecondary,
            borderRadius: dt.radiusPill,
            px: 2.5,
            py: 1,
            transition: "all 0.2s ease",
            width: isPhone ? "100%" : "auto",
            "&:hover": {
              backgroundColor: dt.inputBg,
              color: dt.textPrimary,
            },
          }}
        >
          {translate("common:buttons.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={() => onChoose(selectedReportNumber)}
          disabled={!selectedReportNumber}
          sx={{
            background: `linear-gradient(135deg, ${dt.blue} 0%, #2E86C1 100%)`,
            borderRadius: dt.radiusPill,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "14px",
            fontFamily: dt.font,
            px: 3,
            py: 1,
            boxShadow: "0 2px 8px rgba(52,152,219,0.25)",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            width: isPhone ? "100%" : "auto",
            whiteSpace: "nowrap",
            "&:hover": {
              background: `linear-gradient(135deg, #2E86C1 0%, ${dt.blue} 100%)`,
              transform: "translateY(-2px)",
              boxShadow: "0 6px 20px rgba(52,152,219,0.3)",
            },
            "&.Mui-disabled": {
              background: "#D1D5DB",
              color: "#9CA3AF",
              boxShadow: "none",
            },
          }}
        >
          {translate("common:buttons.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AttachReportDialog;
